import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, PenLine, FileWarning, ReceiptText, Check, SquareKanban, CalendarClock } from "lucide-react";

const ICON = { signature: PenLine, document: FileWarning, invoice: ReceiptText, task: SquareKanban, task_due: CalendarClock };
const COLOR = { signature: "text-primary", document: "text-amber-500", invoice: "text-red-500", task: "text-emerald-500", task_due: "text-orange-500" };

export const NotificationBell = () => {
  const { t, entity, fmt } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ count: 0, items: [] });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!entity) return;
    api.get(`/notifications/summary?entity_id=${entity}`).then((r) => setData(r.data)).catch(() => {});
  }, [entity]);
  useEffect(() => { load(); const h = setInterval(load, 60000); return () => clearInterval(h); }, [load]);

  const markRead = async (e, n) => {
    e.stopPropagation();
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setData((d) => ({ count: d.count - 1, items: d.items.filter((x) => x.id !== n.id) }));
  };
  const go = (n) => { setOpen(false); navigate(n.link); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-button">
          <Bell className="h-4 w-4" />
          {data.count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
              {data.count > 99 ? "99+" : data.count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold">{t("notif.title")}</span>
          <Badge variant="secondary" className="text-[10px]">{data.count}</Badge>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notification-empty">{t("notif.empty")}</div>}
          {data.items.map((n) => {
            const Icon = ICON[n.kind];
            return (
              <button key={`${n.kind}-${n.id}`} onClick={() => go(n)} className="w-full text-left px-4 py-2.5 hover:bg-muted/50 flex items-start gap-3 transition-colors" data-testid={`notification-item-${n.kind}-${n.id}`}>
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${COLOR[n.kind]}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {n.kind === "document" && (n.days_left < 0 ? t("notif.docExpired") : `${t("notif.docExpiring")} ${n.days_left} ${t("dash.daysLeft")}`)}
                    {n.kind === "invoice" && `${t("notif.invoiceOverdue")} ${n.date} · ${fmt(n.total)} · ${n.detail}`}
                    {(n.kind === "signature" || n.kind === "task") && n.detail}
                    {n.kind === "task_due" && (n.days_left < 0 ? `${t("notif.taskOverdue")} ${n.date}` : `${t("notif.taskDue")} ${n.date}`)}
                  </div>
                </div>
                {(n.kind === "signature" || n.kind === "task") && (
                  <span role="button" className="p-1 rounded hover:bg-muted" title={t("dash.markRead")} onClick={(e) => markRead(e, n)} data-testid={`notification-read-${n.id}`}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
